import { useCallback } from 'react';
import { useSelector } from 'react-redux';

const usePermissions = () => {
    const { user } = useSelector((state) => state.auth || state.AuthReducer || {});

    const hasPermission = useCallback((moduleName) => {
        if (!user) return false;
        let roleName = "";
        if (user.role) {
            if (typeof user.role === 'string') roleName = user.role;
            else if (user.role.name) roleName = user.role.name;
            else if (user.role.roleName) roleName = user.role.roleName;
            else if (user.role.title) roleName = user.role.title;
        }
        // Fallback to top-level roleName if present
        if (!roleName && user.roleName) roleName = user.roleName;

        // Extremely robust check for Super Admin or Admin
        const normalizedRole = roleName.toLowerCase().replace(/[^a-z]/g, '');
        const isSuperAdmin =
            Number(user.roleId) === 1 ||
            normalizedRole === 'superadmin' ||
            normalizedRole === 'admin' ||
            normalizedRole === 'super' ||
            normalizedRole === 'root';

        if (isSuperAdmin) {
            return true;
        }

        if (!moduleName || moduleName === 'Dashboard' || moduleName === 'Profile') return true;
        const userPermissions = user.permissions || user.role?.permissions || [];
        if (!Array.isArray(userPermissions) && typeof userPermissions === 'object') {
            return !!userPermissions[moduleName];
        }
        if (Array.isArray(userPermissions)) {
            return userPermissions.some(p => {
                if (typeof p === 'string') return p.toLowerCase() === moduleName.toLowerCase();
                const pModule = p.module || p.name;
                return pModule?.toLowerCase() === moduleName.toLowerCase();
            });
        }
        return false;
    }, [user]);

    /** Check if user has a specific action permission for a module. Permissions shape: { [module]: { [action]: true } } e.g. { settings: { update: true }, episode: { delete: true } } */
    const hasAction = useCallback((moduleName, actionName) => {
        if (!user) return false;
        const normalizedRole = (user.roleName || user.role?.roleName || '').toLowerCase().replace(/[^a-z]/g, '');
        const isSuperAdmin =
            Number(user.roleId) === 1 ||
            normalizedRole === 'superadmin' ||
            normalizedRole === 'admin' ||
            normalizedRole === 'super' ||
            normalizedRole === 'root';
        if (isSuperAdmin) return true;
        const perms = user.permissions || user.role?.permissions;
        if (!perms || typeof perms !== 'object' || Array.isArray(perms)) return false;
        const modulePerms = perms[moduleName];
        if (!modulePerms || typeof modulePerms !== 'object') return false;
        return !!modulePerms[actionName];
    }, [user]);

    return { hasPermission, hasAction, user };
};

export default usePermissions;
